import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {
    Box, Button, Card, Chip, Container, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, Tooltip, Typography
} from '@mui/material';
import Page from '../components/Page';
import * as CommunicationService from '../services/CommunicationService';

// Admin view of the server-side conversions: what was sent to Google Ads / GA4, and why not.

const STATUS_COLOR = {
    PENDING: 'default',
    SENDING: 'info',
    RETRY: 'warning',
    ACCEPTED: 'info',
    DONE: 'success',
    VALIDATED: 'secondary',
    SKIPPED: 'default',
    FAILED: 'error'
};

const STATUS_HELP = {
    PENDING: 'Waiting for the first send (or this destination is switched off).',
    SENDING: 'Being sent right now.',
    RETRY: 'Temporary error, will be retried automatically.',
    ACCEPTED: 'Google Ads accepted the upload; waiting for its processing result.',
    DONE: 'Processed by Google Ads / delivered to GA4.',
    VALIDATED: 'Test mode: Google said the data is valid, nothing was imported. Use "Send" to send it for real.',
    SKIPPED: 'Intentionally not sent (no consent, no identifiers...).',
    FAILED: 'Needs attention: configuration, permission or validation problem.'
};

const RETRYABLE = ['FAILED', 'VALIDATED', 'SKIPPED'];

function formatDate(value) {
    return value ? new Date(value).toLocaleString('ro-RO') : '';
}

function DeliveryCell({conversion, destination, onRetry}) {
    const delivery = destination === 'ADS' ? conversion.ads : conversion.ga4;
    const help = [STATUS_HELP[delivery.status], delivery.detail, delivery.identifiers && `Sent: ${delivery.identifiers}`,
        delivery.nextAttemptAt && `Next: ${formatDate(delivery.nextAttemptAt)}`].filter(Boolean).join('\n');
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={<span style={{whiteSpace: 'pre-line'}}>{help}</span>}>
                <Chip size="small" label={delivery.status} color={STATUS_COLOR[delivery.status] || 'default'}/>
            </Tooltip>
            {RETRYABLE.includes(delivery.status) && (
                <Button size="small" onClick={() => onRetry(conversion.id, destination)}>
                    {delivery.status === 'VALIDATED' ? 'Send' : 'Retry'}
                </Button>
            )}
        </Stack>
    );
}

// What the payment return page saw of the browser Ads purchase tag; compare with the Ads column to explain
// the multi-source "transaction id overlap" diagnostic in Google Ads.
function BrowserTagCell({conversion}) {
    const tag = conversion.browserTag;
    if (!tag) {
        return (
            <Tooltip title="No report: no Cookiebot marketing consent (the browser conversion is not sent), the customer did not reach the payment return page, or it was a staff visit.">
                <Chip size="small" label="no report" variant="outlined"/>
            </Tooltip>
        );
    }
    const matches = tag.hitTransactionId === String(conversion.orderId);
    const label = !tag.tagLoaded ? 'gtag blocked' : !tag.hitSeen ? 'not sent'
        : matches ? `sent · ${tag.hitTransactionId}` : `sent · id ${tag.hitTransactionId || 'missing'}`;
    const color = !tag.hitSeen ? 'error' : matches ? 'success' : 'warning';
    const help = [`Reported: ${formatDate(tag.reportedAt)}`,
        `gtag.js loaded: ${tag.tagLoaded ? 'yes' : 'no'}`,
        `Conversion request seen: ${tag.hitSeen ? 'yes' : 'no'}`,
        tag.hitSeen && `Transaction id sent: ${tag.hitTransactionId || '(none)'}`].filter(Boolean).join('\n');
    return (
        <Tooltip title={<span style={{whiteSpace: 'pre-line'}}>{help}</span>}>
            <Chip size="small" label={label} color={color}/>
        </Tooltip>
    );
}

function Totals({title, counts}) {
    const entries = Object.entries(counts || {});
    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>{title}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
                {entries.length === 0 && <Typography variant="body2" color="text.secondary">No conversions yet</Typography>}
                {entries.map(([status, count]) => (
                    <Chip key={status} size="small" label={`${status}: ${count}`} color={STATUS_COLOR[status] || 'default'}
                          variant="outlined"/>
                ))}
            </Stack>
        </Box>
    );
}

export default function TrackingConversions() {
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        Promise.all([
            CommunicationService.getTrackingSummary(30),
            CommunicationService.getTrackingConversions(page, rowsPerPage)
        ]).then(([summaryResponse, list]) => {
            setSummary(summaryResponse);
            setRows(list.items || []);
            setTotal(list.total || 0);
            setError(null);
        }).catch(failure => setError(failure.message));
    }, [page, rowsPerPage]);

    useEffect(() => { load(); }, [load]);

    const retry = (id, destination) => {
        CommunicationService.retryTrackingDelivery(id, destination).then(load).catch(failure => setError(failure.message));
    };

    const config = summary ? summary.config : null;
    return (
        <Page title="Google conversions">
            <Container maxWidth="xl">
                <Typography variant="h4" gutterBottom>Google conversions (server-side)</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                    One row per paid order. Ads status comes from Google's processing check; GA4 does not confirm
                    processing. Ads attribution itself is visible only in Google Ads.
                </Typography>

                {error && <Typography color="error" sx={{mb: 2}}>{error}</Typography>}

                {summary && (
                    <Card sx={{p: 2, mb: 3}}>
                        <Stack direction={{xs: 'column', md: 'row'}} spacing={4}>
                            <Totals title="Google Ads, last 30 days" counts={summary.ads}/>
                            <Totals title="GA4, last 30 days" counts={summary.ga4}/>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>Settings</Typography>
                                <Typography variant="body2">
                                    Capture: {config.captureEnabled ? 'on' : 'off'} · Start date: {config.startDate || 'not set'}
                                </Typography>
                                <Typography variant="body2">
                                    Ads: {config.adsEnabled ? (config.adsValidateOnly ? 'on (validate only)' : 'on') : 'off'}
                                    {' · '}GA4: {config.ga4Enabled ? (config.ga4Debug ? 'on (debug)' : 'on') : 'off'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Card>
                )}

                <Card>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Order</TableCell>
                                    <TableCell>Paid (detected)</TableCell>
                                    <TableCell>Value</TableCell>
                                    <TableCell>Payment</TableCell>
                                    <TableCell>Last source</TableCell>
                                    <TableCell>First source</TableCell>
                                    <TableCell>Browser tag</TableCell>
                                    <TableCell>Google Ads</TableCell>
                                    <TableCell>GA4</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(row => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.orderId}</TableCell>
                                        <TableCell>{formatDate(row.convertedAt)}</TableCell>
                                        <TableCell>{row.value} {row.currency}</TableCell>
                                        <TableCell>{row.paymentType}{row.orderOrigin === 'GENERATED' ? ' (link)' : ''}</TableCell>
                                        <TableCell>{row.lastSource || (row.linked ? '(direct)' : 'unknown')}
                                            {row.hadAdClick ? ' · ad click' : ''}</TableCell>
                                        <TableCell>{row.firstSource || ''}</TableCell>
                                        <TableCell><BrowserTagCell conversion={row}/></TableCell>
                                        <TableCell><DeliveryCell conversion={row} destination="ADS" onRetry={retry}/></TableCell>
                                        <TableCell><DeliveryCell conversion={row} destination="GA4" onRetry={retry}/></TableCell>
                                    </TableRow>
                                ))}
                                {rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">No conversions recorded yet.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[25, 50, 100]}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={event => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
                    />
                </Card>
            </Container>
        </Page>
    );
}
